/**
 * Life4Earth V2 — React hook for a user's community slot occupancies.
 * DEV-1145 Phase 1j.
 *
 * Pattern follows useApiRoleRhythmInstances. Cache-keyed by user GUID
 * so the same query is shared across consumers (Engagements page,
 * future profile views).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchCommunitySlotOccupancies } from "./communityRoleSlots";
import type { CommunitySlotOccupancyResponse } from "./communityRoleSlots";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiCommunitySlotOccupanciesResult {
  occupancies: CommunitySlotOccupancyResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  refetch: () => void;
}

export function useApiCommunitySlotOccupancies(
  userGuid: string | null | undefined,
): UseApiCommunitySlotOccupanciesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiCommunitySlotOccupanciesResult>({
    occupancies: [],
    loading: !!userGuid,
    error: null,
    source: userGuid ? "loading" : "empty",
    refetch: () => {},
  });

  const cacheKey = userGuid ? `communitySlotOccupancies:${userGuid}` : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;
    try {
      const rows = await fetchForKey<CommunitySlotOccupancyResponse[]>(
        cacheKey!,
        () => fetchCommunitySlotOccupancies(userGuid),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        occupancies: rows,
        loading: false,
        error: null,
        source: rows.length > 0 ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, [userGuid, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) void refetch();

    const onCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === cacheKey) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [userGuid, cacheKey, refetch]);

  return data;
}
